import React, { useState, useRef, useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-types-papi';
import { ALL_FOLDER_ID, SCRAPS_FOLDER_ID } from '@wix/da-url';
import { useFlag } from '@wix/da-shared-react/pkg/redux/flags/hooks/useFlag';
import Flex from '@wix/da-shared-react/pkg/Flex';
import SubfolderCard from '../../SubfolderCard';
import StickyFolderBar from '../../FolderBar/StickyFolderBar';
import { MobileContext } from '@wix/da-shared-react/pkg/Context';

import sOriginal from './FolderDescription.scss';
import sBranched from './FolderDescription.branched.scss';

export interface Props {
  folder: PapiGallection;
  subFolders: PapiGallection[];
  className?: string;
}

const MAX_DESCRIPTION_LENGTH_FOR_MOBILE = 70;

const getDescriptionText = (folder: PapiGallection) => {
  if ([ALL_FOLDER_ID, SCRAPS_FOLDER_ID].includes(folder.folderId)) {
    // pseudo-folders
    return '';
  }

  // Text without line breaks and html tags
  return (folder.description || '').replace(/<\/?[^>]+>/g, '');
};

// TODO: rename to FolderDescriptionCard
export const FolderDescription: React.FC<Props> = ({
  folder,
  subFolders,
  className,
}) => {
  const isMobile = useContext(MobileContext);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const withDesignTweaks = useFlag('little_gallery_redesign');
  const s = withDesignTweaks ? sBranched : sOriginal;

  if (!folder) {
    return null;
  }

  const isDescriptionCroppedOnMobile = () => {
    return (
      getDescriptionText(folder).length > MAX_DESCRIPTION_LENGTH_FOR_MOBILE
    );
  };

  const isDescriptionCroppedOnDesktop = () => {
    if (withDesignTweaks || !wrapRef.current) {
      return false;
    }

    const wrapHeight = wrapRef.current!.getBoundingClientRect().height;
    const descriptionHeight = wrapRef
      .current!.querySelector('p')!
      .getBoundingClientRect().height;
    return wrapHeight < descriptionHeight;
  };

  const renderDescription = () => {
    const description = getDescriptionText(folder);
    if (!description) {
      return null;
    }

    const isDescriptionCropped = isMobile
      ? isDescriptionCroppedOnMobile()
      : isDescriptionCroppedOnDesktop();

    return (
      <div onClick={() => setIsExpanded(!isExpanded)}>
        <div ref={wrapRef} className={classnames(s['description-wrap'])}>
          <p className={s['description']}>
            {isDescriptionCropped && !isExpanded && isMobile
              ? description.slice(0, MAX_DESCRIPTION_LENGTH_FOR_MOBILE)
              : description}
            {isDescriptionCropped && !isExpanded && isMobile && <>... </>}
          </p>
          <div className={s['description-overlay']} />
        </div>
        {isDescriptionCropped && !isExpanded && (
          <a
            className={s['read-more']}
            href=""
            onClick={e => e.preventDefault()}
          >
            {t('pages.gallections.folderDescription.readMore')}
          </a>
        )}
      </div>
    );
  };

  const renderSubfolders = () => {
    if (!subFolders.length) {
      return null;
    }
    return (
      <div className={s['subfolders-section']}>
        <div className={s['subfolders-header']}>
          {t('pages.gallections.subfolders')}
        </div>
        <Flex wrap="wrap">
          {subFolders.map(subFolder => (
            <SubfolderCard
              key={subFolder.folderId}
              subfolder={subFolder}
              className={s['subfolder']}
            />
          ))}
        </Flex>
      </div>
    );
  };

  return (
    <div className={classnames(s['root'], className)}>
      <Flex direction="column">
        {(isMobile || !withDesignTweaks) && (
          <h2 className={s['title']}>{folder.name}</h2>
        )}
        {withDesignTweaks && !isMobile && (
          <StickyFolderBar currentFolder={folder} />
        )}
        {renderDescription()}
        {renderSubfolders()}
      </Flex>
    </div>
  );
};
FolderDescription.displayName = 'FolderDescription';

export default FolderDescription;
